import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Payment as PaymentIcon,
  Close as CloseIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoanApplicationModal from './LoanApplication';
import { trackLoan, getLoanPaymentRequests, getLoansByClientId, api, clientWithdrawLoanApplication, getLmsLoansByUserId, getClientTrackingByUser } from '../api/client';
import { isAuthenticated, getUserInfo } from '../utils/auth';
import { getLoanStatusColor } from '../utils/status';

interface LoanApplication {
  id: string;
  trackingNumber: string | number; // Can be string or number from backend
  loanType?: string;
  amount: number;
  currency: string;
  purpose?: string;
  status: string;
  dateSubmitted: string;
}

interface PaymentRequest {
  id: number;
  amount: number;
  currency: string;
  description: string;
  paymentType: string;
  dueDate?: string;
  status: string;
  isOutstanding: boolean;
  loanId?: string;
  trackingNumber?: string;
}

interface LoanTrackingInfo {
  trackingNumber: string;
  loanId: string;
  clientId: string; // Added clientId field
  clientName: string;
  loanStatus: string;
  loanAmount: number;
  currency: string;
  dateSubmitted: string;
}

const LoanApplications: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoTrackedRef = useRef(false);
  const [activeTab, setActiveTab] = useState(0);
  const [loanApplicationModalOpen, setLoanApplicationModalOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [loanInfo, setLoanInfo] = useState<LoanTrackingInfo | null>(null);
  const [paymentRequests, setPaymentRequests] = useState<PaymentRequest[]>([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState<string | null>(null);
  
  // State for user's loan applications - will be populated when tracking a loan
  const [loanApplications, setLoanApplications] = useState<LoanApplication[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  
  // State for application details modal
  const [selectedApplication, setSelectedApplication] = useState<LoanApplication | null>(null);
  const [applicationDetailsModalOpen, setApplicationDetailsModalOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [confirmWithdrawOpen, setConfirmWithdrawOpen] = useState(false);
  const [pendingWithdrawApp, setPendingWithdrawApp] = useState<LoanApplication | null>(null);

  // NOTE: Auth is enforced globally by ProtectedRoute and axios interceptors;
  // avoid page-level redirects that can clear tokens during long form entry.

  // Effect to automatically add tracked loan to applications when loanInfo changes
  useEffect(() => {
    if (loanInfo && loanApplications.length === 0) {

      addTrackedLoanToApplications();
    }
  }, [loanInfo]);
  
  // Effect to fetch tracked loans on component mount
  useEffect(() => {
    if (isAuthenticated()) {
      fetchUserLoanApplications();
    }
  }, []);

  const handleTrackLoan = async (numberOverride?: string) => {
    // Check authentication before making API call (refresh token may renew expired access)
    if (!isAuthenticated()) {
      navigate('/login', { replace: true });
      return;
    }

    const numberToTrack = (numberOverride ?? trackingNumber).trim();
    if (!numberToTrack) {
      setTrackingError('Please enter a tracking number');
      return;
    }

    if (numberOverride) {
      setTrackingNumber(numberToTrack);
    }

    setTrackingLoading(true);
    setTrackingError(null);
    setLoanInfo(null);
    setPaymentRequests([]);

    try {
      // Track the loan
      const loanData = await trackLoan(numberToTrack);
      setLoanInfo(loanData);

      // Get payment requests for this loan
      const paymentsData = await getLoanPaymentRequests(numberToTrack);
      setPaymentRequests(paymentsData);

      // After successfully tracking a loan, fetch all loans for this user
      // and switch to the "My Applications" tab to show them
      if (loanData.clientId && loanData.clientId !== 'UNKNOWN') {

        await fetchUserLoanApplications();
        setActiveTab(0); // Switch to "My Applications" tab
      } else {
        // If no client ID, still add the tracked loan to applications

        // Wait for loanInfo to be set before calling addTrackedLoanToApplications
        setTimeout(async () => {
          await addTrackedLoanToApplications();
        }, 100);
        setActiveTab(0); // Switch to "My Applications" tab
      }
      
    } catch (error) {
      setTrackingError('Loan not found or tracking number is invalid');
      console.error('Error tracking loan:', error);
    } finally {
      setTrackingLoading(false);
    }
  };

  // Prefill/track from email deep link: /loan-application?trackingNumber=...
  useEffect(() => {
    const fromQuery = searchParams.get('trackingNumber')?.trim();
    if (!fromQuery || autoTrackedRef.current) return;
    autoTrackedRef.current = true;
    setTrackingNumber(fromQuery);
    handleTrackLoan(fromQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Function to fetch all loan applications for the current user
  const fetchUserLoanApplications = async () => {
    try {
      if (!isAuthenticated()) {
        navigate('/login', { replace: true });
        return;
      }
      
      setApplicationsLoading(true);
      setTrackingError(null);
      
      // Get current user info to fetch their loan applications
      const userInfo = getUserInfo();
      if (!userInfo) {
        setApplicationsLoading(false);
        return;
      }
      

      
      // Prefer LMS-backed tracking-style list for accuracy, using single userId (sub)
      try {
        const userId = String((userInfo as any).sub || '');
        if (userId) {
          const trackingList = await getClientTrackingByUser(userId);
          
          // Log the full JSON response before mapping
          console.log('[LOAN_APPLICATIONS] Full JSON response from getClientTrackingByUser:', JSON.stringify(trackingList, null, 2));
          
          if (Array.isArray(trackingList) && trackingList.length > 0) {
            const transformedLoans: LoanApplication[] = trackingList.map((t: any) => {
              // Log each item before transformation
              console.log('[LOAN_APPLICATIONS] Processing loan item:', JSON.stringify(t, null, 2));
              
              // Use direct fields from LoanTracking response
              const amount = Number(t.loanAmount ?? 0) || 0;
              const currency = t.currency || 'USD';
              const status = t.loanStatus || t.pipelineStatus || 'UNKNOWN';
              
              return {
                id: t.loanId || t.id || 'UNKNOWN',
                trackingNumber: t.trackingNumber || 'UNKNOWN',
                loanType: t.loanDetails?.loanProduct || t.loanType || 'Business Loan',
                amount,
                currency,
                purpose: t.loanDetails?.loanPurpose || t.purpose || 'Business expansion',
                status,
                dateSubmitted: t.dateSubmitted || t.createdAt || new Date().toISOString()
              };
            });
            
            // Sort: WITHDRAWN loans at bottom, then by dateSubmitted descending (most recent first)
            transformedLoans.sort((a, b) => {
              const statusA = (a.status || '').toUpperCase();
              const statusB = (b.status || '').toUpperCase();
              const isWithdrawnA = statusA === 'WITHDRAWN';
              const isWithdrawnB = statusB === 'WITHDRAWN';
              
              // If one is withdrawn and the other isn't, put withdrawn at bottom
              if (isWithdrawnA && !isWithdrawnB) return 1;
              if (!isWithdrawnA && isWithdrawnB) return -1;
              
              // Both have same withdrawn status, sort by date descending
              const dateA = new Date(a.dateSubmitted).getTime();
              const dateB = new Date(b.dateSubmitted).getTime();
              return dateB - dateA; // Descending order
            });
            
            console.log('[LOAN_APPLICATIONS] Transformed loans:', JSON.stringify(transformedLoans, null, 2));
            setLoanApplications(transformedLoans);
            return;
          }
        }
      } catch (err) {
        console.error('Error fetching LMS client tracking by user:', err);
      }

      // Fallback: use LMS loans-by-user endpoint if available
      try {
        const fallbackUserId = String((userInfo as any).sub || '');
        if (fallbackUserId) {
          const lmsLoans = await getLmsLoansByUserId(fallbackUserId);
          
          // Log the full JSON response before mapping
          console.log('[LOAN_APPLICATIONS] Full JSON response from getLmsLoansByUserId:', JSON.stringify(lmsLoans, null, 2));
          
          if (Array.isArray(lmsLoans) && lmsLoans.length > 0) {
            const transformedLoans: LoanApplication[] = lmsLoans.map((t: any) => {
              // Log each item before transformation
              console.log('[LOAN_APPLICATIONS] Processing loan item (fallback):', JSON.stringify(t, null, 2));
              
              // Use direct fields from LoanApplicationDto structure
              // LoanApplicationDto has: trackingNumber, loanInfo (with loanAmount, currency, loanPurpose, loanProduct)
              const amount = Number(
                t.loanInfo?.loanAmount ??
                t.loanAmount ??
                t.loanDetails?.loanAmount ??
                0
              ) || 0;
              const currency = (
                t.loanInfo?.currency ||
                t.currency ||
                t.loanDetails?.currency ||
                'USD'
              );
              // Status from LoanApplicationDto (now includes loanStatus and pipelineStatus)
              const status = t.loanStatus || t.pipelineStatus || t.loanProcess?.loanStatus || t.status || 'UNKNOWN';
              
              return {
                id: t.loanId || t.id || String(t.trackingNumber) || 'UNKNOWN',
                trackingNumber: t.trackingNumber || 'UNKNOWN',
                loanType: t.loanInfo?.loanProduct || t.loanDetails?.loanProduct || t.loanType || 'Business Loan',
                amount,
                currency: typeof currency === 'string' ? currency : (currency?.code || currency?.name || 'USD'),
                purpose: t.loanInfo?.loanPurpose || t.loanDetails?.loanPurpose || t.purpose || 'Business expansion',
                status,
                dateSubmitted: t.createdAt ? (typeof t.createdAt === 'string' ? t.createdAt : new Date(t.createdAt).toISOString()) : (t.dateSubmitted || new Date().toISOString())
              };
            });
            
            // Sort: WITHDRAWN loans at bottom, then by dateSubmitted descending (most recent first)
            transformedLoans.sort((a, b) => {
              const statusA = (a.status || '').toUpperCase();
              const statusB = (b.status || '').toUpperCase();
              const isWithdrawnA = statusA === 'WITHDRAWN';
              const isWithdrawnB = statusB === 'WITHDRAWN';
              
              // If one is withdrawn and the other isn't, put withdrawn at bottom
              if (isWithdrawnA && !isWithdrawnB) return 1;
              if (!isWithdrawnA && isWithdrawnB) return -1;
              
              // Both have same withdrawn status, sort by date descending
              const dateA = new Date(a.dateSubmitted).getTime();
              const dateB = new Date(b.dateSubmitted).getTime();
              return dateB - dateA; // Descending order
            });
            
            console.log('[LOAN_APPLICATIONS] Transformed loans (fallback):', JSON.stringify(transformedLoans, null, 2));
            setLoanApplications(transformedLoans);
            return;
          }
        }
      } catch (err) {
        console.error('Error fetching LMS loans by userId:', err);
      }

      // Do not fallback to single tracking-number flows for the main list
    } catch (err: any) {
      console.error('Error fetching user loan applications:', err);
      setTrackingError('Failed to fetch loan applications. Please try again.');
    } finally {
      setApplicationsLoading(false);
    }
  };

  const handleWithdrawApplication = async (application: LoanApplication) => {
    if (!application?.trackingNumber) return;
    try {
      setWithdrawing(true);
      setWithdrawError(null);
      const userInfo = getUserInfo();
      const userId = userInfo?.sub || userInfo?.userId;
      const response = await clientWithdrawLoanApplication({
        trackingNumber: String(application.trackingNumber),
        userId: String(userId || ''),
      });
      
      // Check if response indicates success
      if (response?.success || response?.message) {
        // Success - refresh list and update UI
        await fetchUserLoanApplications();
        // Update selected dialog info if open
        if (selectedApplication && selectedApplication.id === application.id) {
          setSelectedApplication({ ...selectedApplication, status: 'WITHDRAWN' });
        }
        // Close confirmation dialog
        setConfirmWithdrawOpen(false);
        setPendingWithdrawApp(null);
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const errorData = e?.response?.data;
      const errorMsg = typeof errorData === 'string' ? errorData : (errorData?.message || errorData?.error || '');
      
      // If it's a 400 but the message indicates the loan was already withdrawn or similar, treat as success
      if (status === 400) {
        const lowerMsg = errorMsg.toLowerCase();
        if (lowerMsg.includes('already withdrawn') || 
            lowerMsg.includes('cannot be withdrawn') && lowerMsg.includes('current status') ||
            lowerMsg.includes('withdrawn')) {
          // Loan might already be withdrawn or in a state that prevents withdrawal
          // Refresh the list to get updated status - LMS callback might have already updated it
          await fetchUserLoanApplications();
          setConfirmWithdrawOpen(false);
          setPendingWithdrawApp(null);
          // Don't show error - the loan is likely already withdrawn
          return;
        }
      }
      
      // For other errors, show the error message
      const msg = errorMsg || e?.message || 'Failed to withdraw application';
      setWithdrawError(String(msg));
    } finally {
      setWithdrawing(false);
    }
  };

  // Helper function to add the tracked loan to applications
  const addTrackedLoanToApplications = async () => {

    
    if (!loanInfo) {

      return;
    }
    

    
    const mockApplication: LoanApplication = {
      id: loanInfo.loanId,
      trackingNumber: loanInfo.trackingNumber,
      loanType: 'Business Loan',
      amount: loanInfo.loanAmount,
      currency: loanInfo.currency,
      purpose: 'Business expansion',
      status: loanInfo.loanStatus,
      dateSubmitted: loanInfo.dateSubmitted
    };



    // Check if this application is already in the list
    const existingIndex = loanApplications.findIndex(app => app.id === mockApplication.id);
    if (existingIndex === -1) {

      setLoanApplications(prev => {
        const newList = [mockApplication, ...prev];

        return newList;
      });
    } else {

      // Update existing application with latest info
      setLoanApplications(prev => prev.map(app => 
        app.id === mockApplication.id ? mockApplication : app
      ));
    }
  };

  const handleMakePayment = (paymentRequest: PaymentRequest) => {
    navigate('/payments', { 
      state: { 
        loanId: loanInfo?.loanId,
        trackingNumber: trackingNumber,
        paymentRequest: paymentRequest
      } 
    });
  };

  const handleViewApplicationDetails = (application: LoanApplication) => {
    // Show loan application details in a modal instead of redirecting to Fineract
    setSelectedApplication(application);
    setApplicationDetailsModalOpen(true);
  };

  const handleUpdateApplication = async (application: LoanApplication) => {
    try {
      setApplicationsLoading(true);
      
      // Determine the loan ID - check multiple possible fields in order of preference
      let loanId: any = null;
      if (application.id !== undefined && application.id !== null) {
        loanId = application.id;
      } else if ((application as any).loanId !== undefined && (application as any).loanId !== null) {
        loanId = (application as any).loanId;
      } else if ((application as any).clientLoanId !== undefined && (application as any).clientLoanId !== null) {
        loanId = (application as any).clientLoanId;
      } else if ((application as any).applicationId !== undefined && (application as any).applicationId !== null) {
        loanId = (application as any).applicationId;
      }
      
      // Get tracking number as fallback
      const trackingNumber = application.trackingNumber || (application as any).trackingNumber;
      
      // If no loanId but we have tracking number, try to fetch by tracking number
      if (!loanId && trackingNumber) {
        try {
          // Try the track endpoint first
          try {
            const response = await api.get(`/api/v1/client-loans/track/${trackingNumber}`);
            const loanByTracking = response.data;
            loanId = loanByTracking.id || loanByTracking.loanId || (loanByTracking as any).clientLoanId;
          } catch (trackError) {
            // Fallback: try fetching all loans and find by tracking number
            const allLoansResponse = await api.get('/api/v1/client-loans');
            const allLoans = Array.isArray(allLoansResponse.data) ? allLoansResponse.data : [];
            const matchingLoan = allLoans.find((loan: any) => 
              loan.trackingNumber?.toString() === trackingNumber.toString() ||
              loan.trackingNumber === trackingNumber
            );
            if (matchingLoan) {
              loanId = matchingLoan.id || matchingLoan.loanId || matchingLoan.clientLoanId;
            }
          }
        } catch (trackingError) {
          console.error('[handleUpdateApplication] Failed to fetch by tracking number:', trackingError);
        }
      }
      
      if (!loanId) {
        setTrackingError('Unable to determine loan ID. Please refresh the page and try again.');
        return;
      }
      
      // Ensure loanId is a valid number
      const numericLoanId = typeof loanId === 'string' ? parseInt(loanId, 10) : Number(loanId);
      if (isNaN(numericLoanId) || !isFinite(numericLoanId)) {
        setTrackingError('Invalid loan ID. Please refresh the page and try again.');
        return;
      }
      
      // Navigate to the separate loan update page
      navigate(`/loan-update/${numericLoanId}`);

    } catch (error) {
      console.error('Error preparing loan update:', error);
      setTrackingError('Failed to prepare loan update. Please try again.');
    } finally {
      setApplicationsLoading(false);
    }
  };

  // Check if loan application can be updated (status is "requesting for more info")
  // This determines if we should use the standalone document upload modal
  const canUpdateApplication = (application: LoanApplication) => {
    if (!application) return false;
    
    // Get status from multiple possible fields (status or loanStatus)
    const status = application.status || (application as any).loanStatus || '';
    const statusLower = status?.toLowerCase() || '';
    
    // Check for all possible "needs more info" status variations
    const needsMoreInfoStatuses = [
      'MORE_INFO_REQUESTED',
      'more_info_requested',
      'requesting for more info',
      'requesting more info',
      'additional info required',
      'pending additional information',
      'more info requested',
      'needs more information',
      'needs more info'
    ];
    
    return status === 'MORE_INFO_REQUESTED' || 
           needsMoreInfoStatuses.includes(statusLower);
  };

  const handleMakeApplicationPayment = (application: LoanApplication) => {
    // Navigate to payments page with loan application context
    navigate('/payments', { 
      state: { 
        loanId: application.id,
        trackingNumber: application.trackingNumber,
        isApplicationPayment: true,
        applicationStatus: application.status
      } 
    });
  };

  const getStatusColor = (status: string) => getLoanStatusColor(status);

  const handleApplicationSuccess = () => {
    // Refresh loan applications list
    // TODO: Implement API call to refresh the list

  };

  // Function to refresh applications list
  const refreshApplications = async () => {
    // Check authentication before making API call
    if (!isAuthenticated()) {

      navigate('/login', { replace: true });
      return;
    }


    // Always try to fetch tracked loans from database
    await fetchUserLoanApplications();
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Loan Applications
      </Typography>

      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="My Applications" />
        <Tab label="Track Loan" />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Your Loan Applications ({loanApplications.length})
            </Typography>
            <Box>
              <Button
                variant="outlined"
                onClick={refreshApplications}
                disabled={applicationsLoading}
                sx={{ mr: 2 }}
              >
                {applicationsLoading ? <CircularProgress size={20} /> : 'Refresh'}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setLoanApplicationModalOpen(true)}
              >
                New Application
              </Button>
            </Box>
          </Box>

          {applicationsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

                     {!applicationsLoading && loanApplications.length > 0 && (
             <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
               <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Tracking #</TableCell>
                    <TableCell>Loan Type</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Purpose</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date Submitted</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                                     {loanApplications.map((application, index) => (
                     <TableRow 
                       key={application.id}
                       sx={{ 
                         '&:nth-of-type(odd)': { backgroundColor: 'grey.50' },
                         '&:hover': { backgroundColor: 'grey.100' },
                         transition: 'background-color 0.2s ease'
                       }}
                     >
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {application.trackingNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>{application.loanType}</TableCell>
                      <TableCell>
                        {application.currency}{' '}
                        {application.amount != null
                          ? application.amount.toLocaleString()
                          : '-'}
                      </TableCell>
                      <TableCell>{application.purpose}</TableCell>
                      <TableCell>
                        <Chip 
                          label={application.status} 
                          color={getStatusColor(application.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{new Date(application.dateSubmitted).toLocaleDateString()}</TableCell>
                                             <TableCell>
                         <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                           <Button
                             variant="outlined"
                             size="small"
                             onClick={() => handleViewApplicationDetails(application)}
                             startIcon={<ViewIcon />}
                             sx={{ 
                               minWidth: 'auto',
                               px: 1.5,
                               py: 0.5,
                               fontSize: '0.75rem',
                               borderColor: 'primary.main',
                               color: 'primary.main',
                               '&:hover': {
                                 borderColor: 'primary.dark',
                                 backgroundColor: 'primary.light',
                                 color: 'primary.dark'
                               }
                             }}
                           >
                             View
                           </Button>
                           {canUpdateApplication(application) && (
                             <Button
                               variant="contained"
                               size="small"
                               onClick={() => handleUpdateApplication(application)}
                               startIcon={applicationsLoading ? <CircularProgress size={16} /> : <EditIcon />}
                               color="info"
                               disabled={applicationsLoading}
                               sx={{ 
                                 minWidth: 'auto',
                                 px: 1.5,
                                 py: 0.5,
                                 fontSize: '0.75rem',
                                 fontWeight: 600,
                                 boxShadow: 2,
                                 '&:hover': {
                                   boxShadow: 4,
                                   transform: 'translateY(-1px)'
                                 }
                               }}
                             >
                               {applicationsLoading ? 'Loading...' : 'Update'}
                             </Button>
                           )}
                           {application.status === 'PENDING_APPLICATION_FEE' && (
                             <Button
                               variant="contained"
                               size="small"
                               onClick={() => handleMakeApplicationPayment(application)}
                               startIcon={<PaymentIcon />}
                               color="warning"
                               sx={{ 
                                 minWidth: 'auto',
                                 px: 1.5,
                                 py: 0.5,
                                 fontSize: '0.75rem',
                                 fontWeight: 600,
                                 boxShadow: 2,
                                 '&:hover': {
                                   boxShadow: 4,
                                   backgroundColor: 'warning.dark'
                                 }
                               }}
                             >
                               Pay Fee
                             </Button>
                           )}
                         </Box>
                       </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

                     {!applicationsLoading && loanApplications.length === 0 && (
             <Paper sx={{ p: 4, textAlign: 'center' }}>
               <Typography variant="h6" color="textSecondary" gutterBottom>
                 No loan applications found
               </Typography>
               <Typography color="textSecondary" sx={{ mb: 2 }}>
                 {loanInfo 
                   ? "Track a loan first to see your applications here."
                   : "You haven't submitted any loan applications yet."
                 }
               </Typography>
               
               
               <Button
                 variant="contained"
                 startIcon={<AddIcon />}
                 onClick={() => setLoanApplicationModalOpen(true)}
               >
                 Apply for Your First Loan
               </Button>
             </Paper>
           )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Track Your Loan Application
          </Typography>
          
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Enter Tracking Number"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="e.g., TRK001"
                    helperText="Enter the tracking number you received when you applied for a loan"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleTrackLoan}
                    disabled={trackingLoading || !trackingNumber.trim()}
                    startIcon={trackingLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                  >
                    {trackingLoading ? 'Tracking...' : 'Track Loan'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {trackingError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {trackingError}
            </Alert>
          )}

          {loanInfo && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Loan Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Tracking Number</Typography>
                    <Typography variant="body1" fontFamily="monospace">{loanInfo.trackingNumber}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Loan ID</Typography>
                    <Typography variant="body1">{loanInfo.loanId}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Client Name</Typography>
                    <Typography variant="body1">{loanInfo.clientName}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Loan Status</Typography>
                    <Chip 
                      label={loanInfo.loanStatus} 
                      color={getStatusColor(loanInfo.loanStatus) as any}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Loan Amount</Typography>
                    <Typography variant="body1">
                      {loanInfo.currency}{' '}
                      {loanInfo.loanAmount != null
                        ? loanInfo.loanAmount.toLocaleString()
                        : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Date Submitted</Typography>
                    <Typography variant="body1">{new Date(loanInfo.dateSubmitted).toLocaleDateString()}</Typography>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="info.contrastText">
                    💡 <strong>Tip:</strong> This loan has been added to your applications! 
                    Switch to the "My Applications" tab to see all your loans.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {paymentRequests.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Outstanding Payments
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell sx={{ minWidth: 200 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentRequests.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>
                            <Chip 
                              label={payment.paymentType} 
                              size="small"
                              color={payment.paymentType === 'Processing Fee' ? 'warning' : 'info'}
                            />
                          </TableCell>
                          <TableCell>{payment.description}</TableCell>
                          <TableCell>
                            {payment.currency}{' '}
                            {payment.amount != null
                              ? payment.amount.toLocaleString()
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {payment.dueDate ? new Date(payment.dueDate).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={payment.status} 
                              size="small"
                              color={payment.isOutstanding ? 'error' : 'success'}
                            />
                          </TableCell>
                          <TableCell>
                            {payment.isOutstanding && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleMakePayment(payment)}
                              >
                                Make Payment
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Loan Application Modal */}
      <LoanApplicationModal
        open={loanApplicationModalOpen}
        onClose={() => setLoanApplicationModalOpen(false)}
        onSubmitSuccess={handleApplicationSuccess}
      />

      {/* Application Details Modal - View Only */}
      <Dialog 
        open={applicationDetailsModalOpen} 
        onClose={() => setApplicationDetailsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Loan Application Details
          <IconButton
            aria-label="close"
            onClick={() => setApplicationDetailsModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Tracking Number</Typography>
                <Typography variant="body1" fontFamily="monospace">{selectedApplication.trackingNumber}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Loan Type</Typography>
                <Typography variant="body1">{selectedApplication.loanType}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Amount</Typography>
                <Typography variant="body1">{selectedApplication.currency} {selectedApplication.amount?.toLocaleString() || '0'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Purpose</Typography>
                <Typography variant="body1">{selectedApplication.purpose}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip 
                  label={selectedApplication.status} 
                  color={getStatusColor(selectedApplication.status) as any}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Date Submitted</Typography>
                <Typography variant="body1">{new Date(selectedApplication.dateSubmitted).toLocaleDateString()}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {withdrawError && (
            <Alert severity="error" sx={{ width: '100%', mb: 1 }}>{withdrawError}</Alert>
          )}
          {selectedApplication && !['WITHDRAWN','APPROVED','ACTIVE','REJECTED'].includes(selectedApplication.status) && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CloseIcon />}
              disabled={withdrawing}
              onClick={() => {
                setPendingWithdrawApp(selectedApplication);
                setConfirmWithdrawOpen(true);
              }}
            >
              {withdrawing ? 'Withdrawing...' : 'Withdraw Application'}
            </Button>
          )}
          {selectedApplication?.status === 'PENDING_APPLICATION_FEE' && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<PaymentIcon />}
              onClick={() => {
                setApplicationDetailsModalOpen(false);
                handleMakeApplicationPayment(selectedApplication);
              }}
            >
              Make Application Fee Payment
            </Button>
          )}
          <Button onClick={() => setApplicationDetailsModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Withdraw Dialog */}
      <Dialog
        open={confirmWithdrawOpen}
        onClose={() => setConfirmWithdrawOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Withdrawal</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to withdraw this loan application?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmWithdrawOpen(false)} variant="text">
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (pendingWithdrawApp) {
                await handleWithdrawApplication(pendingWithdrawApp);
              }
              setConfirmWithdrawOpen(false);
              setPendingWithdrawApp(null);
            }}
            variant="contained"
            color="error"
            disabled={withdrawing}
          >
            {withdrawing ? 'Withdrawing...' : 'Confirm Withdraw'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoanApplications;



